import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronRight, FiSearch, FiLock, FiArrowLeft } from "react-icons/fi";
import { TbQrcode } from "react-icons/tb";
import { RiBankLine } from "react-icons/ri";
import "./Send.css";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { useLanguage } from "../../contexts/LanguageContext";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";


const formatCoins = (n, digits = 2) =>
  Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

/**
 * Props (all optional):
 * - currentCoins: number (default 0)
 * - requiredCoins: number (default 100)
 * - onSelect?: (key: "qr"|"search"|"private"|"withdraw") => void
 */
export default function Send({
  currentCoins = 0,
  requiredCoins = 100,
  onSelect,
}) {
  const navigate = useNavigate();
  const [userWinicoins, setUserWinicoins] = useState(0);
  const [loading, setLoading] = useState(true);
  const { translations: t } = useLanguage();
  const { currentUser, userProfile } = useAuth();

  
  // Mark wallet as verified when entering send page
  React.useEffect(() => {
    sessionStorage.setItem('walletVerified', 'true');
  }, []);
  
  const asNumber = (v) => {
    const n = typeof v === "string" ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fetchWinicoinsBalance = useCallback(async (userId) => {
    // Prefer wallet_view if it exists (may be already decoded)
    try {
      const { data, error } = await supabase
        .from("wallet_view")
        .select("winicoins")
        .eq("user_id", userId)
        .single();
      if (!error && data && data.winicoins != null) return asNumber(data.winicoins);
    } catch {
      // ignore
    }

    const { data, error } = await supabase
      .from("wallet")
      .select("winicoins")
      .eq("user_id", userId)
      .single();

    if (error || !data) return 0;
    const raw = data.winicoins;
    if (typeof raw === "number") return raw;
    if (typeof raw === "string") return asNumber(raw);
    if (raw instanceof Uint8Array) {
      try {
        const decoded = new TextDecoder().decode(raw);
        return asNumber(decoded);
      } catch {
        return 0;
      }
    }
    return 0;
  }, []);

  // Fetch user's winicoins from Supabase wallet_view/wallet
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentUser?.id) {
        setUserWinicoins(0);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const bal = await fetchWinicoinsBalance(currentUser.id);
        if (!cancelled) setUserWinicoins(bal);
      } catch (e) {
        console.error("[Send] Error fetching winicoins:", e);
        if (!cancelled) setUserWinicoins(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, fetchWinicoinsBalance]);
  
  const progressPct = Math.max(
    0,
    Math.min(100, (Number(userWinicoins) / Number(requiredCoins || 1)) * 100)
  );

  const items = [
    { key: "qr",      title: t.send?.items?.qr      || "Send via QR",        icon: <TbQrcode /> },
    { key: "search",  title: t.send?.items?.search  || "Search for User",    icon: <FiSearch /> },
    { key: "private", title: t.send?.items?.private || "Private Transaction", icon: <FiLock /> },
  ];
  

  const handleClick = (key) => {
    if (onSelect) onSelect(key);
    // Default navigations — change to your routes if needed
    if (key === "qr") navigate("/send/qr-scan");
    if (key === "search") navigate("/send/search");
    // /send/private has no page — navigation disabled
    // Note: withdraw is handled separately and doesn't navigate
  };

  const handleWithdrawClick = () => {
    // Withdraw button is not clickable - just show the summary
    console.log("Withdraw requires 100 winicoins minimum");
  };

  if (loading) {
    return (
      <div className="send-page">
        <div className="send-content">
        <div className="loading">{t.send?.loading || "Loading..."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="send-page">
      <FloatingSidebarButton userProfile={userProfile} />
      {/* Top bar */}
      <div className="send-topbar">
        <button className="send-back" onClick={() => navigate(-1)} aria-label="Back">
          <FiArrowLeft />
        </button>
        <h1 className="send-title">{t.send?.title || "Send from Wallet"}</h1>
        <div className="send-topbar-spacer" />
      </div>

      <div className="send-content">
      <h2 className="send-section-title">{t.send?.sectionTitle || "Send Options"}</h2>

        <div className="send-card">
          <div className="send-list">
            {items.map((it, idx) => (
              <button
                key={it.key}
                className={`send-row ${idx !== items.length - 1 ? "with-border" : ""}`}
                onClick={() => handleClick(it.key)}
              >
                <div className="row-left">
                  <div className="icon-badge" aria-hidden="true">
                    {it.icon}
                  </div>
                  <span className="row-title">{it.title}</span>
                </div>
                <div className="row-right">
                  <FiChevronRight className="chev" />
                </div>
              </button>
            ))}
          </div>

          {/* Withdraw button with integrated summary */}
          <div className="withdraw-section">
            <button
              className="withdraw-button"
              onClick={handleWithdrawClick}
              disabled={userWinicoins < requiredCoins}
            >
              <div className="withdraw-content">
                <div className="withdraw-left">
                  <div className="icon-badge withdraw-icon" aria-hidden="true">
                    <RiBankLine />
                  </div>
                  <div className="withdraw-text">
                  <span className="withdraw-title">
                    {t.send?.withdraw?.button || "Withdraw from Wallet"}
                  </span>
                  <span className="withdraw-subtitle">
                    {userWinicoins >= requiredCoins
                      ? (t.send?.withdraw?.ready || "Ready to withdraw")
                      : `${formatCoins(requiredCoins - userWinicoins, 0)} ${t.send?.withdraw?.moreNeeded || "more winicoins needed"}`
                    }
                  </span>
                  </div>
                </div>
                <div className="withdraw-right">
                  {userWinicoins >= requiredCoins ? (
                    <FiChevronRight className="chev" />
                  ) : (
                    <span className="withdraw-locked">🔒</span>
                  )}
                </div>
              </div>
            </button>

            {/* Progress and summary integrated with withdraw button */}
            <div className="withdraw-summary">
              <div className="summary-line">
              <span className="label">{t.send?.withdraw?.current || "Current:"}</span>
              <span className="value">
                {formatCoins(userWinicoins, 2)} {t.send?.withdraw?.unit || "winicoins"}
              </span>
              </div>

              <div className="progress-track" role="progressbar"
                   aria-valuemin={0}
                   aria-valuemax={requiredCoins}
                   aria-valuenow={Math.min(userWinicoins, requiredCoins)}>
                <div
                  className="progress-fill"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="summary-line">
              <span className="label">{t.send?.withdraw?.required || "Required:"}</span>
              <span className="value">
                {formatCoins(requiredCoins, 1)} {t.send?.withdraw?.unit || "winicoins"}
              </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
